import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { ProjectForm } from '@/components/forms/ProjectForm'
import { apiClient } from '@/lib/api-client'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'react-hot-toast'

export function ProjectsPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [selectedProject, setSelectedProject] = useState<any>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const ITEMS_PER_PAGE = 12

  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiClient.get('/projects')
      return response.data
    }
  })

  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId: number) => {
      await apiClient.delete(`/projects/${projectId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('Project deleted successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete project')
    }
  })

  const handleEdit = (project: any) => {
    setSelectedProject(project)
    setIsFormOpen(true)
  }

  const handleDelete = async (projectId: number) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      deleteProjectMutation.mutate(projectId)
    }
  }

  const handleFormSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['projects'] })
    setIsFormOpen(false)
    setSelectedProject(null)
  }

  // Paginate projects
  const totalPages = Math.ceil((projects?.length || 0) / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedProjects = projects?.slice(startIndex, endIndex) || []

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setSelectedProject(null)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border-b border-border/70 pb-4">
        <h1 className="text-xl font-semibold tracking-tight">Projects</h1>
        {user?.role === 'Admin' && (
          <Button onClick={() => setIsFormOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add project
          </Button>
        )}
      </div>

      <div className="data-panel">
        {paginatedProjects.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-muted-foreground">
            No projects found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Reporting person</th>
                  <th>Members</th>
                  <th>Created</th>
                  {user?.role === 'Admin' && <th className="text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedProjects.map((project: any) => (
                  <tr key={project.id}>
                    <td>
                      <p className="font-medium">{project.name}</p>
                      <p className="max-w-md truncate text-xs text-muted-foreground">
                        {project.description || 'No description'}
                      </p>
                    </td>
                    <td data-label="Reporting person">{project.reportingPerson?.name || '—'}</td>
                    <td data-label="Members">{project.employees?.length || 0}</td>
                    <td data-label="Created" className="text-muted-foreground">{new Date(project.createdAt).toLocaleDateString()}</td>
                    {user?.role === 'Admin' && (
                      <td className="is-actions">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(project)} aria-label="Edit project">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDelete(project.id)}
                            disabled={deleteProjectMutation.isPending}
                            aria-label="Delete project"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-3 py-2 text-sm text-muted-foreground">
            <span>
              {startIndex + 1}–{Math.min(endIndex, projects?.length || 0)} of {projects?.length || 0}
            </span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="h-8" disabled={currentPage === 1} onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-8" disabled={currentPage === totalPages} onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <ProjectForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        project={selectedProject}
        onSuccess={handleFormSuccess}
      />
    </div>
  )
}
